/**
 * What a request wishes of an arrangement beyond its players: a key, a tempo, a manner of
 * playing, an easier page.
 *
 * Each is what words ask for and no choice of the arranger's answers -- "a tone higher", "in D
 * minor", "faster", "at 90", "pizzicato", "the cello plucked", "easy enough for a beginner" --
 * and each holds whatever the arranging starts from, but for the tune, which takes the key and
 * the tempo and neither of the other two. None of them is a note: the key moves the score
 * before a note of it is read, the tempo changes the marks of its speed and nothing else, and
 * the manner and the ease say how the notes chosen are played and where a player is seated.
 * The pipeline's `api/wished` reads them, and its `theory/rk_keep_playing` and
 * `theory/rk_keep_ease` do them.
 */

/**
 * How the players sound their notes: as written, or one way throughout. `pizzicato` is a bowed
 * string's, plucked and seated in its plucked compass; `muted` a bowed string's and a brass
 * player's; `staccato` anybody's, a dot on every note of a quarter or shorter; `legato`
 * anybody's but a plucked or struck player's, a note slurred to the next within a bar. A player
 * the manner is not for plays as written; where none asked for can play it, it is refused.
 */
export const PLAYINGS = ["written", "pizzicato", "staccato", "legato", "muted"] as const;
export type Playing = (typeof PLAYINGS)[number];

/**
 * How hard the page may be to play. `easier` seats a player of one line no higher than the top
 * of its best register, gives a hand an octave and a note fewer than it has fingers, and writes
 * no double stops; no rhythm is made simpler and no note of the tune left out.
 */
export const EASES = ["written", "easier"] as const;
export type Ease = (typeof EASES)[number];

/** Every tonic a key is asked for by: a letter, sharpened or flattened once. */
export const TONICS = [
  "C",
  "C#",
  "Db",
  "D",
  "D#",
  "Eb",
  "E",
  "F",
  "F#",
  "Gb",
  "G",
  "G#",
  "Ab",
  "A",
  "A#",
  "Bb",
  "B",
] as const;
export type Tonic = (typeof TONICS)[number];

/** The furthest a key moves a score, in semitones, either way. */
export const KEY_FURTHEST = 12;

/**
 * The key a score is arranged in: moved `by` semitones, up where more than 0; or into the key
 * of `tonic`, by the shortest way from the key the score is heard in. Where `minor` names the
 * mode the score is not in, the piece keeps its mode and is moved to the key with the
 * signature asked for: D minor of a piece in C major is F major. `null` leaves the mode unsaid.
 */
export type ArrangeKey = { by: number } | { tonic: Tonic; minor: boolean | null };

/** What the key a request asked for did to the score, as the arrangement says it. */
export interface MovedKey {
  /** Semitones the score was moved, up where more than 0; 0 where it was in the key already. */
  by: number;
  /** The key the score was heard in, as "D major". */
  was: string;
  /** The key it is arranged in. */
  now: string;
  /** All of it in a sentence, beginning in lower case. */
  said: string;
}

/** The slowest and the fastest a tempo may be asked for, in quarter notes a minute. */
export const TEMPO_SLOWEST = 20;
export const TEMPO_FASTEST = 400;

/** The most a tempo may be multiplied or divided by. */
export const TEMPO_FURTHEST = 4;

/**
 * The speed a score is played at: its opening at `bpm` quarter notes a minute, or every speed
 * it marks `times` as fast. Every later mark moves in proportion to the opening's, and a score
 * that marks none is taken at 120, as every player plays an unmarked score, and marked at bar 1.
 * Only the marks change: no note, and no rule or verdict, reads the tempo.
 */
export type ArrangeTempo = { bpm: number } | { times: number };

/** What the tempo a request asked for did to the score, as the arrangement says it. */
export interface ChangedTempo {
  /** The opening's quarter notes a minute as the score marked it; `null` where it marked none. */
  was: number | null;
  /** The opening's quarter notes a minute now. */
  now: number;
  /** How many later changes of tempo the score marks, each moved in proportion. */
  changes: number;
  /** All of it in a sentence, beginning in lower case. */
  said: string;
}

/** The wishes as a request carries them, every one of them left out where nothing is asked. */
export interface ArrangeWishes {
  /** How the players sound their notes. `written` when absent. */
  playing?: Playing;
  /**
   * The instruments `playing` is asked of, as `roster` names them; every player when absent
   * or empty. Refused where `playing` is `written`.
   */
  playingWho?: readonly string[];
  /** How hard the page may be to play. `written` when absent; not with `stops`. */
  ease?: Ease;
  /** The key to arrange in. The score's own when absent. */
  key?: ArrangeKey;
  /** The tempo to arrange at. The score's own when absent. */
  tempo?: ArrangeTempo;
}
